import React, { useState, useEffect } from "react";
import {
  Container,
  Form,
  Button,
  Card,
  Row,
  Col,
  Alert,
  Spinner,
  ProgressBar,
  Modal,
} from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";
import { cairoDistricts, visitTypes } from "../models/models";
import { addReservation } from "../services/firebaseService";
import LocationMap from "./LocationMap";
import {
  collection,
  getDocs,
  query,
  where,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { projectData } from "../services/Constants";
import { useTranslation } from "react-i18next";
import i18next from "i18next";

// Time slots mapping for display purposes
const TIME_SLOT_LABELS = {
  "8-10": "8:00 AM - 10:00 AM",
  "10-12": "10:00 AM - 12:00 PM",
  "12-14": "12:00 PM - 2:00 PM",
  "14-16": "2:00 PM - 4:00 PM",
  "16-18": "4:00 PM - 6:00 PM",
  "18-20": "6:00 PM - 8:00 PM",
  "20-22": "8:00 PM - 10:00 PM",
};

const PatientForm = () => {
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingSummary, setBookingSummary] = useState(null);
  const [error, setError] = useState(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [locationAvailable, setLocationAvailable] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const { t } = useTranslation();

  // Language selection state
  const [showLanguageModal, setShowLanguageModal] = useState(false);

  // New state for availability
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(false);

  // New state for step wizard
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  // Reset function to start over
  const resetForm = () => {
    setBookingComplete(false);
    setBookingSummary(null);
    setCurrentStep(1); // Reset to step 1
    setUserLocation(null);
    setLocationAvailable(false);
    setAvailableTimeSlots([]);
  };
  const convertArabicToEnglishNumbers = (str) => {
    if (!str) return str;
    const arabicNumerals = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
    return str.replace(/[٠-٩]/g, (match) => arabicNumerals.indexOf(match));
  };

  // Fixed validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    // age: Yup.number()
    //   .required("Age is required")
    //   .positive("Age must be positive")
    //   .integer("Age must be a whole number"),
    age: Yup.string()
      .required("Age is required")
      .test(
        "is-positive-integer",
        "Age must be a positive whole number",
        function (value) {
          const convertedValue = convertArabicToEnglishNumbers(value);
          const numValue = Number(convertedValue);
          return !isNaN(numValue) && Number.isInteger(numValue) && numValue > 0;
        },
      ),
    gender: Yup.string().required("Gender is required"),
    // phone: Yup.string()
    //   .required("Phone number is required")
    //   .matches(/^[0-9]{11}$/, "Phone number must be 11 digits"),

    // phone: Yup.string()
    //   .required("Phone number is required")
    //   .test(
    //     "is-valid-phone",
    //     "Phone number must be 11 digits",
    //     function (value) {
    //       return /^[0-9]{11}$/.test(value);
    //     }
    //   ),
    phone: Yup.string()
      .required("Phone number is required")
      .test(
        "is-valid-phone",
        "Phone number must be 11 digits",
        function (value) {
          const convertedValue = convertArabicToEnglishNumbers(value);
          return /^[0-9]{11}$/.test(convertedValue);
        },
      ),
    visitType: Yup.string().required("Visit type is required"),
    useCurrentLocation: Yup.boolean(),
    district: Yup.string().when("useCurrentLocation", {
      is: false,
      then: () =>
        Yup.string().required(
          "District is required when not using current location",
        ),
      otherwise: () => Yup.string().notRequired(),
    }),
    address: Yup.string().when("useCurrentLocation", {
      is: false,
      then: () =>
        Yup.string().required(
          "Address details are required when not using current location",
        ),
      otherwise: () => Yup.string().notRequired(),
    }),
    appointmentDate: Yup.date().when("visitType", {
      is: (value) => value === visitTypes.SCHEDULED,
      then: () =>
        Yup.date()
          .required("Appointment date is required for scheduled visits")
          .min(new Date(), t("VisitDateVerification")),
      otherwise: () => Yup.date().notRequired(),
    }),
    appointmentTime: Yup.string().when("visitType", {
      is: (value) => value === visitTypes.SCHEDULED,
      then: () =>
        Yup.string().required(
          "Appointment time is required for scheduled visits",
        ),
      otherwise: () => Yup.string().notRequired(),
    }),
  });

  const initialValues = {
    name: "",
    age: "",
    gender: "",
    phone: "",
    visitType: visitTypes.SCHEDULED,
    useCurrentLocation: false,
    district: "",
    address: "",
    appointmentDate: "",
    appointmentTime: "",
  };

  // Fetch available dates from Firestore
  useEffect(() => {
    fetchAvailableDates();
  }, []);

  // Language selection popup
  useEffect(() => {
    const preferredLanguage = localStorage.getItem("preferredLanguage");
    if (!preferredLanguage) {
      setShowLanguageModal(true);
    } else {
      i18next.changeLanguage(preferredLanguage);
    }
  }, []);

  const handleLanguageSelection = (language) => {
    localStorage.setItem("preferredLanguage", language);
    i18next.changeLanguage(language);
    setShowLanguageModal(false);
  };

  const fetchAvailableDates = async () => {
    try {
      setLoadingAvailability(true);

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const availabilityRef = collection(
        db,
        projectData().AVAILABILITY_COLLECTION,
      );
      // Get only dates from today forward
      const querySnapshot = await getDocs(availabilityRef);

      const dates = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const date = data.date.toDate();

        // Only include future dates
        if (date >= today) {
          dates.push({
            id: doc.id,
            date: date,
            timeSlots: data.timeSlots || [],
          });
        }
      });

      // Sort dates chronologically
      dates.sort((a, b) => a.date - b.date);
      setAvailableDates(dates);
    } catch (err) {
      console.error("Error fetching available dates:", err);
      setError("Failed to load available appointment dates. Please try again.");
    } finally {
      setLoadingAvailability(false);
    }
  };

  // Update available time slots when a date is selected
  const handleDateChange = (selectedDate, setFieldValue) => {
    setFieldValue("appointmentDate", selectedDate);
    setFieldValue("appointmentTime", ""); // Reset time when date changes

    // Check if selected date is today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDateObj = new Date(selectedDate);
    selectedDateObj.setHours(0, 0, 0, 0);

    if (selectedDateObj.getTime() === today.getTime()) {
      // Date is today - show alert and force urgent visit
      setError(
        t("SameDayVisitUrgent") ||
          "Same-day visits must be booked as urgent visits. Scheduling cannot be done on the same day.",
      );
      setFieldValue("visitType", visitTypes.URGENT);
    } else {
      // Clear error if selecting a future date
      setError(null);
    }

    // Find time slots for the selected date
    const dateObj = new Date(selectedDate);
    const matchingDate = availableDates.find(
      (item) => item.date.toDateString() === dateObj.toDateString(),
    );

    if (matchingDate) {
      setAvailableTimeSlots(matchingDate.timeSlots);
    } else {
      setAvailableTimeSlots([]);
    }
  };

  // Function to get user's current location
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      return;
    }

    setIsLoadingLocation(true);
    setLocationAvailable(false);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Success handler
        const { latitude, longitude } = position.coords;
        setUserLocation({ latitude, longitude });
        setLocationAvailable(true);
        setIsLoadingLocation(false);
      },
      (error) => {
        // Error handler
        console.error("Error getting location:", error);
        setError(
          "Failed to get your location. Please try again or enter your address manually.",
        );
        setIsLoadingLocation(false);
        setLocationAvailable(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      },
    );
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setError(null);

      // Format data for Firestore
      const reservationData = {
        ...values,
        // Convert date strings to proper format
        appointmentDate: values.appointmentDate
          ? new Date(values.appointmentDate).toISOString()
          : null,
      };

      // Add location coordinates if using current location
      if (values.useCurrentLocation && userLocation) {
        reservationData.location = userLocation;
      }

      // Save to Firestore
      const savedReservation = await addReservation(reservationData);

      // Prepare summary for WhatsApp
      const summary = generateSummary(values);
      setBookingSummary(summary);
      setBookingComplete(true);
      resetForm();
    } catch (err) {
      console.error("Error saving reservation:", err);
      setError("Failed to submit your reservation. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const formatTimeSlot = (timeSlot) => {
    if (!timeSlot || !TIME_SLOT_LABELS[timeSlot]) return "";

    const [start, end] = TIME_SLOT_LABELS[timeSlot].split(" - ");
    if (i18next.language === "ar") {
      return `من ${start.replace("AM", "ص").replace("PM", "م")} إلى ${end.replace("AM", "ص").replace("PM", "م")}`;
    } else {
      return `from ${start} to ${end}`;
    }
  };

  const generateSummary = (values) => {
    const location = values.useCurrentLocation
      ? "Current location (GPS)"
      : `District: ${values.district}, Address: ${values.address}`;

    const visitDetails =
      values.visitType === visitTypes.URGENT
        ? "URGENT VISIT NEEDED"
        : `${t("Scheduled for")}: ${new Date(
            values.appointmentDate,
          ).toLocaleDateString(i18next.language === "ar" ? "ar-EG" : "en-GB", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
          })} ${t("at")}: ${formatTimeSlot(values.appointmentTime)}`;

    return {
      text: `
* ${t("DoctorHomeVisitRequest")} *
---------------------------
${t("Patient")}: ${values.name}
${t("Age")}: ${values.age}
${t("Gender")}: ${values.gender}
${t("Phone")}: ${values.phone}
---------------------------
${t(visitDetails)}
---------------------------
${t("Location")}: ${location}
---------------------------
`,
      phoneNumber: values.phone,
    };
  };

  const sendToWhatsApp = () => {
    if (!bookingSummary) return;

    // Format WhatsApp message
    const encodedText = encodeURIComponent(bookingSummary.text);
    const whatsappUrl = `https://wa.me/${
      projectData().callCenterPhone
    }?text=${encodedText}`;

    // Open WhatsApp in a new window
    window.open(whatsappUrl, "_blank");
  };

  // Helper functions for step navigation
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  // Function to check if current step is valid before proceeding
  const validateCurrentStep = (values) => {
    let valid = true;
    let errorMessages = [];

    // Validate step 1 - Patient Information
    if (currentStep === 1) {
      if (!values.name) {
        valid = false;
        errorMessages.push("Name is required");
      }
      if (!values.age) {
        valid = false;
        errorMessages.push("Age is required");
      }
      if (!values.gender) {
        valid = false;
        errorMessages.push("Gender is required");
      }
      if (!values.phone) {
        valid = false;
        errorMessages.push("Phone number is required");
      } else if (!/^[0-9]{11}$/.test(values.phone)) {
        valid = false;
        errorMessages.push("Phone number must be 11 digits");
      }
    }

    // Validate step 2 - Visit Information
    else if (currentStep === 2) {
      if (values.visitType === visitTypes.SCHEDULED) {
        if (!values.appointmentDate) {
          valid = false;
          errorMessages.push("Please select an appointment date");
        }
        if (!values.appointmentTime) {
          valid = false;
          errorMessages.push("Please select an appointment time");
        }
      }
    }

    // Validate step 3 - Location Information
    else if (currentStep === 3) {
      if (!values.useCurrentLocation) {
        if (!values.district) {
          valid = false;
          errorMessages.push("Please select a district");
        }
        if (!values.address) {
          valid = false;
          errorMessages.push("Please provide address details");
        }
      } else if (!locationAvailable) {
        valid = false;
        errorMessages.push("Please wait for location detection to complete");
      }
    }

    if (!valid) {
      setError(errorMessages.join(", "));
      setTimeout(() => setError(null), 4000);
    } else {
      setError(null);
    }

    return valid;
  };

  if (bookingComplete) {
    return (
      <Container className="mt-5">
        <Card>
          <Card.Header as="h4" className="text-center bg-success text-white">
            {t("BookingComplete")}
          </Card.Header>
          <Card.Body>
            <h5>{t("BookingSubmitted")}</h5>
            <Card.Text className="mb-3">{t("WhatsAppSummary")}</Card.Text>
            <Card.Text as="div">
              <pre style={{ whiteSpace: "pre-wrap" }}>
                {bookingSummary?.text}
              </pre>
            </Card.Text>
            <div className="d-flex justify-content-center mt-4">
              <style>{`
  @keyframes whatsappGlow {
    from {
      box-shadow: 0 0 10px rgba(37, 211, 102, 0.5);
    }
    to {
      box-shadow: 0 0 20px rgba(37, 211, 102, 0.8), 0 0 30px rgba(37, 211, 102, 0.6);
    }
  }
  .whatsapp-glow-button {
    animation: whatsappGlow 0.5s infinite alternate;
    transition: all 0.5s ease;
    margin: 0 10px;
  }
  .whatsapp-glow-button:hover {
    box-shadow: 0 0 30px rgba(37, 211, 102, 0.9), 0 0 40px rgba(37, 211, 102, 0.7) !important;
  }
`}</style>
              <Button
                variant="success"
                size="lg"
                onClick={sendToWhatsApp}
                className="me-2 whatsapp-glow-button"
              >
                <i className="bi bi-whatsapp me-2"></i>
                {t("SendToWhatsApp")}
              </Button>
              <Button variant="primary" size="lg" onClick={resetForm}>
                {t("BookAnother")}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  // Step titles for display
  const stepTitles = [
    t("PatientInformation"),
    t("VisitInformation"),
    t("LocationInformation"),
  ];

  // Format date for display in the date picker
  const formatDateForPicker = (date) => {
    return date.toISOString().split("T")[0];
  };

  return (
    <>
      {/* Language Selection Modal */}
      <Modal
        show={showLanguageModal}
        onHide={() => {}}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton={false}>
          <Modal.Title>Select Language / اختر اللغة</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <p style={{ fontSize: "1rem", marginBottom: "30px" }}>
            Please select your preferred language
            <br />
            من فضلك اختر اللغة المفضلة لديك
          </p>
          <Row className="g-3">
            <Col md={6}>
              <Button
                variant="primary"
                size="lg"
                style={{ width: "100%" }}
                onClick={() => handleLanguageSelection("en")}
              >
                🇬🇧 English
              </Button>
            </Col>
            <Col md={6}>
              <Button
                variant="success"
                size="lg"
                style={{ width: "100%" }}
                onClick={() => handleLanguageSelection("ar")}
              >
                🇪🇬 العربية
              </Button>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>

      <Container className="my-5">
        <Card>
          <Card.Header as="h3" className="text-center bg-primary text-white">
            {t("DoctorVisitBooking")}
          </Card.Header>

          <Card.Body>
            {/* Progress indicator */}
            <div className="mb-4">
              <ProgressBar
                now={(currentStep / totalSteps) * 100}
                label={`${t("Step")} ${currentStep} ${t("Of")} ${totalSteps}`}
                variant="info"
                className="mb-2"
              />
              <h4 className="text-center">
                {t("Step")} {currentStep}: {stepTitles[currentStep - 1]}
              </h4>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                setFieldValue,
              }) => (
                <Form onSubmit={handleSubmit} autoComplete="off" noValidate>
                  {/* Step 1: Patient Information */}
                  {currentStep === 1 && (
                    <>
                      <Row className="mb-3">
                        <Col md={6}>
                          <Form.Group controlId="name">
                            <Form.Label>{t("FullName")}</Form.Label>
                            <Form.Control
                              type="text"
                              name="name"
                              value={values.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.name && errors.name}
                              autoComplete="name"
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.name}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="phone">
                            <Form.Label>{t("Phone")}</Form.Label>
                            <Form.Control
                              type="tel"
                              name="phone"
                              value={values.phone}
                              // onChange={handleChange}
                              onChange={(e) => {
                                // Convert Arabic numerals to English digits immediately
                                const convertedValue =
                                  convertArabicToEnglishNumbers(e.target.value);
                                setFieldValue("phone", convertedValue);
                              }}
                              onBlur={handleBlur}
                              isInvalid={touched.phone && errors.phone}
                              placeholder="e.g., 01234567890"
                              autoComplete="tel"
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.phone}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={6}>
                          <Form.Group controlId="age">
                            <Form.Label>{t("Age")}</Form.Label>
                            <Form.Control
                              type="text"
                              name="age"
                              value={values.age}
                              onChange={(e) => {
                                // Convert Arabic numerals to English digits immediately
                                const convertedValue =
                                  convertArabicToEnglishNumbers(e.target.value);
                                setFieldValue("age", convertedValue);
                              }}
                              onBlur={handleBlur}
                              isInvalid={touched.age && errors.age}
                              autoComplete="off"
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.age}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group controlId="gender">
                            <Form.Label>{t("Gender")}</Form.Label>
                            <Form.Select
                              name="gender"
                              value={values.gender}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              isInvalid={touched.gender && errors.gender}
                              autoComplete="sex"
                            >
                              <option value="">{t("SelectGender")}</option>
                              <option value="male">{t("Male")}</option>
                              <option value="female">{t("Female")}</option>
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                              {errors.gender}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      </Row>
                    </>
                  )}

                  {/* Step 2: Visit Information */}
                  {currentStep === 2 && (
                    <>
                      <Row className="mb-3">
                        <Col>
                          <Form.Group controlId="visitType">
                            <Form.Label>{t("VisitType")}</Form.Label>
                            <div>
                              <Form.Check
                                inline
                                type="radio"
                                label={t("ScheduledVisit")}
                                name="visitType"
                                id="scheduledVisit"
                                value={visitTypes.SCHEDULED}
                                checked={
                                  values.visitType === visitTypes.SCHEDULED
                                }
                                onChange={handleChange}
                                className={
                                  i18next.language === "ar"
                                    ? "form-check-reverse"
                                    : ""
                                }
                              />
                              <br />
                              <Form.Check
                                inline
                                type="radio"
                                label={t("UrgentVisit")}
                                name="visitType"
                                id="urgentVisit"
                                value={visitTypes.URGENT}
                                checked={values.visitType === visitTypes.URGENT}
                                onChange={handleChange}
                                className={
                                  i18next.language === "ar"
                                    ? "form-check-reverse"
                                    : ""
                                }
                              />
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>

                      {values.visitType === visitTypes.SCHEDULED && (
                        <>
                          {loadingAvailability ? (
                            <div className="text-center my-3">
                              <Spinner animation="border" size="sm" />
                              <span className="ms-2">{t("LoadingDates")}</span>
                            </div>
                          ) : availableDates.length === 0 ? (
                            <Alert variant="info">
                              {t("NoAppointmentSlots")}
                            </Alert>
                          ) : (
                            <Row className="mb-3">
                              <Col md={6}>
                                <Form.Group controlId="appointmentDate">
                                  <Form.Label>{t("SelectDate")}</Form.Label>
                                  <Form.Select
                                    name="appointmentDate"
                                    value={values.appointmentDate}
                                    onChange={(e) =>
                                      handleDateChange(
                                        e.target.value,
                                        setFieldValue,
                                      )
                                    }
                                    onBlur={handleBlur}
                                    isInvalid={
                                      touched.appointmentDate &&
                                      errors.appointmentDate
                                    }
                                  >
                                    <option value="">{t("SelectADate")}</option>
                                    {availableDates.map((item) => (
                                      <option
                                        key={item.id}
                                        value={formatDateForPicker(item.date)}
                                      >
                                        {item.date.toLocaleDateString(
                                          i18next.language === "ar"
                                            ? "ar-EG"
                                            : "en-GB",
                                          {
                                            weekday: "long",
                                            year: "numeric",
                                            month: "long",
                                            day: "numeric",
                                          },
                                        )}
                                      </option>
                                    ))}
                                  </Form.Select>
                                  <Form.Control.Feedback type="invalid">
                                    {errors.appointmentDate}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group controlId="appointmentTime">
                                  <Form.Label>{t("SelectTime")}</Form.Label>
                                  <Form.Select
                                    name="appointmentTime"
                                    value={values.appointmentTime}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    isInvalid={
                                      touched.appointmentTime &&
                                      errors.appointmentTime
                                    }
                                    disabled={
                                      !values.appointmentDate ||
                                      availableTimeSlots.length === 0
                                    }
                                  >
                                    <option value="">{t("SelectATime")}</option>
                                    {availableTimeSlots.map((slotId) => (
                                      <option key={slotId} value={slotId}>
                                        {t(`Time${slotId.replace("-", "To")}`)}
                                      </option>
                                    ))}
                                  </Form.Select>
                                  <Form.Control.Feedback type="invalid">
                                    {errors.appointmentTime}
                                  </Form.Control.Feedback>
                                </Form.Group>
                              </Col>
                            </Row>
                          )}
                        </>
                      )}
                    </>
                  )}

                  {/* Step 3: Location Information */}
                  {currentStep === 3 && (
                    <>
                      <Row className="mb-3">
                        <Col>
                          <Form.Group controlId="useCurrentLocation">
                            <div className="d-flex align-items-center">
                              <Form.Check
                                type="checkbox"
                                label={t("UseMyLocation")}
                                name="useCurrentLocation"
                                checked={values.useCurrentLocation}
                                onChange={(e) => {
                                  setFieldValue(
                                    "useCurrentLocation",
                                    e.target.checked,
                                  );
                                  if (e.target.checked) {
                                    // Clear address fields
                                    setFieldValue("district", "");
                                    setFieldValue("address", "");
                                    // Get current location
                                    getCurrentLocation();
                                  } else {
                                    setLocationAvailable(false);
                                    setUserLocation(null);
                                  }
                                }}
                              />
                              {values.useCurrentLocation &&
                                isLoadingLocation && (
                                  <Spinner
                                    animation="border"
                                    variant="primary"
                                    size="sm"
                                    className="ms-2"
                                  />
                                )}
                              {values.useCurrentLocation &&
                                locationAvailable && (
                                  <span className="ms-2 text-success">
                                    <svg
                                      width="16"
                                      height="16"
                                      fill="currentColor"
                                      className="bi bi-check-circle-fill"
                                      viewBox="0 0 16 16"
                                    >
                                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                                    </svg>
                                    <span className="ms-1">
                                      {t("LocationDetected")}
                                    </span>
                                  </span>
                                )}
                            </div>
                            {values.useCurrentLocation &&
                              locationAvailable &&
                              userLocation && (
                                <small className="text-muted d-block mt-1">
                                  {t("CoordinatesText")}:{" "}
                                  {userLocation.latitude.toFixed(6)},{" "}
                                  {userLocation.longitude.toFixed(6)}
                                </small>
                              )}
                          </Form.Group>
                        </Col>
                      </Row>

                      {/* Show map when location is available */}
                      {values.useCurrentLocation &&
                        locationAvailable &&
                        userLocation && (
                          <Row className="mb-4 mt-2">
                            <Col>
                              <Card>
                                <Card.Body>
                                  <Card.Title as="h6">
                                    {t("YourLocation")}
                                  </Card.Title>
                                  <LocationMap
                                    location={userLocation}
                                    height="300px"
                                  />
                                </Card.Body>
                              </Card>
                            </Col>
                          </Row>
                        )}

                      {!values.useCurrentLocation && (
                        <>
                          <Row className="mb-3">
                            <Col>
                              <Form.Group controlId="district">
                                <Form.Label>{t("District")}</Form.Label>
                                <Form.Select
                                  name="district"
                                  value={values.district}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  isInvalid={
                                    touched.district && errors.district
                                  }
                                >
                                  <option value="">
                                    {t("SelectDistrict")}
                                  </option>
                                  {cairoDistricts[i18next.language].map(
                                    (district) => (
                                      <option key={district} value={district}>
                                        {district}
                                      </option>
                                    ),
                                  )}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                  {errors.district}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>
                          <Row className="mb-3">
                            <Col>
                              <Form.Group controlId="address">
                                <Form.Label>{t("AddressDetails")}</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={3}
                                  name="address"
                                  value={values.address}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  isInvalid={touched.address && errors.address}
                                  placeholder={t("AddressPlaceholder")}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors.address}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>
                          </Row>
                        </>
                      )}
                    </>
                  )}

                  {/* Navigation buttons */}
                  <div className="d-flex justify-content-between mt-4">
                    {currentStep > 1 && (
                      <Button
                        variant="secondary"
                        onClick={prevStep}
                        disabled={isSubmitting}
                      >
                        {t("Back")}
                      </Button>
                    )}

                    <div className="ms-auto">
                      {currentStep < totalSteps ? (
                        <Button
                          variant="primary"
                          onClick={() => {
                            if (validateCurrentStep(values)) {
                              nextStep();
                            }
                          }}
                          disabled={isSubmitting}
                        >
                          {t("Next")}
                        </Button>
                      ) : (
                        <Button
                          variant="success"
                          type="submit"
                          disabled={
                            isSubmitting ||
                            (values.useCurrentLocation && !locationAvailable)
                          }
                        >
                          {isSubmitting ? t("Submitting") : t("BookHomeVisit")}
                        </Button>
                      )}
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default PatientForm;
